/*
 * Copyright (c) 2023 Anvilor Solutions LLC - All Rights Reserved.
 *
 * The information and source code contained herein is the exclusive property of
 * Anvilor Solutions LLC and may not be disclosed, examined, or reproduced in whole
 * or in part without the explicit written authorization from Anvilor Solutions LLC.
 */

import { ControlDefinition } from "@anvilor/ngx-rui";
import { LegacyRuiControl } from "@anvilor/ngx-rui";
import { RuiStore } from "@anvilor/ngx-rui";
import { Observable } from "rxjs";
import { CONTROL_SUBMIT_BUTTON } from "../submit-button-control/submit-button-control";
import { ThemeBasedAsset } from "@anvilor/ngx-theming";
import { distinctUntilChangedJson } from "@anvilor/ngx-core-utils";
import { map } from "rxjs/operators";

export const CONTROL_FORM_LAYOUT = "anvilor_forms:form_layout";

export interface FormLayoutControlDefinition extends ControlDefinition {
  control: typeof CONTROL_FORM_LAYOUT;
  submit_button_label?: string;
  title?: string;
  body: ControlDefinition;
  header_logo?: string | ThemeBasedAsset;
  success_message_title?: string;
  success_message_body?: string;
  disable_automatic_toasts?: boolean;
}

export class FormLayoutControl extends LegacyRuiControl<FormLayoutControlDefinition> {
  bodyControl$: Observable<LegacyRuiControl>;
  submitButtonControl$: Observable<LegacyRuiControl>;

  title$: Observable<string | undefined>;
  headerLogo$: Observable<string | ThemeBasedAsset | null>;
  disableAutomaticToasts$: Observable<boolean>;
  successMessageTitle$: Observable<string>;
  successMessageBody$: Observable<string>;

  constructor(store: RuiStore, id: string) {
    super(store, id);
    this.title$ = this.evaluatedSelect((d) => d.title);

    this.submitButtonControl$ = this.rawDefinitionSelect(
      (d) => d.submit_button_label ?? "Submit"
    ).pipe(
      distinctUntilChangedJson(),
      map((button_label) =>
        this.store.controls.addNewControl({
          definition: {
            control: CONTROL_SUBMIT_BUTTON,
            button_label,
          },
          parent_iid: this.iid,
        })
      )
    );

    this.bodyControl$ = this.rawDefinitionSelect((d) => d.body).pipe(
      distinctUntilChangedJson(),
      map((definition) =>
        this.store.controls.addNewControl({ definition, parent_iid: this.iid })
      )
    );

    this.headerLogo$ = this.evaluatedSelect((d) => d.header_logo ?? null);
    this.successMessageTitle$ = this.evaluatedSelect(
      (d) => d.success_message_title ?? "Success!"
    );
    this.successMessageBody$ = this.evaluatedSelect(
      (d) => d.success_message_body ?? "Response recorded."
    );
    this.disableAutomaticToasts$ = this.evaluatedSelect(
      (d) => d.disable_automatic_toasts ?? false
    );
  }
}
